import type {
  DataLayerEventListenerProps,
  DataLayerLoggingPermissions,
  EmptyObject,
} from '@snapchat/logging';
import { AbstractDataLayerEventListener } from '@snapchat/logging';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

const gtmScriptId = 'gtm-script';

/**
 * Type for events that are OK to send through GTM. These can be any object type.
 *
 * See: https://developers.google.com/tag-platform/tag-manager/datalayer
 *
 * TODO: Change the type to accept actual event params. However it's hard to make the generic work
 * with the existing GTM definitions in the web package.
 */
// biome-ignore lint/suspicious/noExplicitAny: TODO write reason
export type GtmEvent = Partial<Record<string, any>>;

export interface GoogleTagManagerClientEventListenerProps<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject
> extends Omit<DataLayerEventListenerProps<LoggingContext, CustomEvents, GtmEvent>, 'dataLayer'> {
  gtmId: string;
  nonce?: string;
}

/**
 * Adds the {eventCallback} and {eventTimeout} contol parameters to a GTM event.
 *
 * See https://developers.google.com/analytics/devguides/migration/ecommerce/gtm-ga4-to-ua
 */
export function addGtmEventCallback(event: GtmEvent, callback: () => void): boolean {
  if (typeof event !== 'object') {
    throw new Error('Unexpected event for GTM. GTM events are objects.');
  }

  // TODO: Merge eventCallback if there's one already specified in the event.

  event.eventCallback = callback;
  event.eventTimeout = 2_000 /* ms */;
  return true;
}

/**
 * Event listener that sends events to Google Tag Manager.
 *
 * Requires the caller to format their own events (sadly) since GTM can be configured with any
 * object.
 *
 * TODO: find out why child container nonce isn't set properly
 * https://jira.sc-corp.net/browse/ENTWEB-8257
 */
export class GoogleTagManagerClientEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends DataLayerLoggingPermissions = DataLayerLoggingPermissions
> extends AbstractDataLayerEventListener<LoggingContext, CustomEvents, LoggingPermission> {
  private readonly gtmProps: GoogleTagManagerClientEventListenerProps<LoggingContext, CustomEvents>;

  constructor(props: GoogleTagManagerClientEventListenerProps<LoggingContext, CustomEvents>) {
    super(props);
    this.gtmProps = props;
  }

  /** @override addEventToDataLayer */
  protected addEventToDataLayer(event: GtmEvent): void {
    window.dataLayer!.push(event);
  }

  /** @override init */
  protected init(): Promise<void> {
    window.dataLayer = window.dataLayer ?? [];

    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmProps.gtmId}`;
    gtmScript.id = gtmScriptId;

    if (this.gtmProps.nonce) gtmScript.nonce = this.gtmProps.nonce;

    document.head.appendChild(gtmScript);

    // We need to configure datalayer with a custom record due to GTM requiring gtm.start
    // https://developers.google.com/tag-platform/tag-manager/web/datalayer
    this.addEventToDataLayer({ 'gtm.start': Date.now(), event: 'gtm.js' });

    return Promise.resolve();
  }

  /** @override */
  protected addEventCallback = addGtmEventCallback;
}
